import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import {
  pollingAllowed,
  userActive,
  useResumeRefresh,
} from "@/components/hooks/activityGate";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import FolderSharedOutlinedIcon from "@mui/icons-material/FolderSharedOutlined";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import ContactSupportOutlinedIcon from "@mui/icons-material/ContactSupportOutlined";
import VpnKeyOutlinedIcon from "@mui/icons-material/VpnKeyOutlined";
import NotificationsOutlinedIcon from "@mui/icons-material/NotificationsOutlined";
import StorageOutlinedIcon from "@mui/icons-material/StorageOutlined";
import FunctionsIcon from "@mui/icons-material/Functions";
import LayersOutlinedIcon from "@mui/icons-material/LayersOutlined";
import PublicOutlinedIcon from "@mui/icons-material/PublicOutlined";
import AdminPanelSettingsOutlinedIcon from "@mui/icons-material/AdminPanelSettingsOutlined";
import ListAltOutlinedIcon from "@mui/icons-material/ListAltOutlined";
import PeopleOutlineIcon from "@mui/icons-material/PeopleOutlined";
import CorporateFareOutlinedIcon from "@mui/icons-material/CorporateFareOutlined";
import PersonOffOutlinedIcon from "@mui/icons-material/PersonOffOutlined";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import TroubleshootOutlinedIcon from "@mui/icons-material/TroubleshootOutlined";
import QueryStatsOutlinedIcon from "@mui/icons-material/QueryStatsOutlined";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { Badge } from "@mui/material";
import { useAuth } from "contexts/AuthProvider";
import { UserAccountResponse } from "components/entities";
import {
  NotificationStatus,
  NotificationTypeJS,
  Notification,
} from "target_wasm";
import { useNavigation } from "contexts/NavigationProvider";
import { fetchAccountDetails } from "./models/ModelServices";
import { useBoundStore } from "components/canvas/stores/useAppStore";
import { SidebarItems } from "./SidebarItems";
import type { SidebarItem } from "./SidebarItems";

import { UserProfileItem } from "./UserProfileItem";
import { RailToggle } from "./RailToggle";
import { railName, type ShellPageKey } from "@/lib/navigation/pages";
import { NotificationDropdown } from "./notifications/NotificationDropdown";
import { fetchNotifications } from "./notifications/NotificationsApi";
import { Divider } from "components/common/Divider";
import { useShallow } from "zustand/react/shallow";
import { useSupportStore } from "stores/useSupportStore";
import { StorageIndicator } from "./StorageIndicator";
import { nearestLimit } from "./nearestLimit";
import { SettingsDialog } from "./SettingsDialog";
import { OrgSwitcher } from "@/components/main/spaces/OrgSwitcher";
import {
  SpacesFoldToggle,
  SpacesRail,
  SpacesRootActions,
} from "@/components/main/spaces/SpacesRail";
import { SpacesFilterField } from "@/components/main/spaces/SpacesFilter";
import { useSpacesFilterStore } from "@/components/main/spaces/spacesFilterStore";
import {
  spaceColor,
  spaceSubtree,
  useEnsureOrgContext,
  useOrgContextStore,
  useVisibleSpacePaths,
} from "@/components/main/stores/orgContextStore";
import { SpaceDot } from "@/components/main/spaces/SpaceDot";
import { useSharedWithMeStore } from "@/components/main/stores/sharedWithMeStore";
import { scanAccessCards } from "@/components/main/utils/notifications/accessCardRefresh";
import {
  partitionSharedModels,
  selectExternalModels,
  sharedWithMeCount,
} from "@/components/main/sharedWithMe/selectors";
import { useMainStateStore } from "@/components/main/stores/mainStateStore";
import "./common/VersionChip.css";

const QUOTA_WARNING_PERCENT = 80;

/// A space grant is in both: it brings the space AND the models inside it.
const SPACE_LIST_CARDS = [NotificationTypeJS.SpaceAccess] as const;
const MODEL_LIST_CARDS = [
  NotificationTypeJS.SpaceAccess,
  NotificationTypeJS.ModelShared,
  NotificationTypeJS.AccessRevokedFromModel,
  NotificationTypeJS.ModelDeleted,
] as const;

interface SidebarProps {
  isOpen: boolean;
  toggleSidebar: () => void;
}

export const Sidebar: React.FC<SidebarProps> = ({ isOpen, toggleSidebar }) => {
  const sidebarWidthVar = isOpen
    ? "var(--sidebar-width-open)"
    : "var(--sidebar-width-closed)";
  const { selectedPage, navigateToPage } = useNavigation();

  const { logout, email, role, canManagePublicCatalog } = useAuth();
  const {
    activeOrgId,
    selectedSpaceId,
    setSelectedSpace,
    treeCollapsed,
    usage,
  } = useOrgContextStore(
    useShallow((state) => ({
      activeOrgId: state.activeOrgId,
      selectedSpaceId: state.selectedSpaceId,
      setSelectedSpace: state.setSelectedSpace,
      treeCollapsed: state.treeCollapsed,
      usage: state.usage,
    })),
  );
  useEnsureOrgContext();
  const invalidateModels = useMainStateStore((state) => state.invalidateModels);
  // An org switch re-scopes the whole models list.
  useEffect(() => {
    if (activeOrgId) void invalidateModels();
  }, [activeOrgId, invalidateModels]);
  const socket = useBoundStore((state) => state.websocket);

  const [showNotificationsDropdown, setShowNotificationsDropdown] =
    useState(false);
  const [notificationsDropdownPersistent, setNotificationsDropdownPersistent] =
    useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [accountDetails, setAccountDetails] = useState<
    UserAccountResponse | undefined
  >(undefined);
  const openSupport = useSupportStore((s) => s.openSupport);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const notificationsButtonRef = useRef<HTMLButtonElement>(null);
  const navScrollRef = useRef<HTMLDivElement>(null);
  const dropdownTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );
  const notificationsTimeoutRef = useRef<
    ReturnType<typeof setTimeout> | undefined
  >(undefined);
  const intervalRef = useRef<number | null>(null);
  const storageIntervalRef = useRef<number | null>(null);
  const seenAccessCardsRef = useRef<Set<string> | null>(null);

  const loadNotifications = async () => {
    try {
      const fetchedNotifications = await fetchNotifications();
      setNotifications(fetchedNotifications);
      dropRevokedOrgs(fetchedNotifications);
      refreshOnAccessCards(fetchedNotifications);
    } catch (error) {
      console.error("Failed to fetch notifications:", error);
    }
  };

  /// The org list is fetched only at init (polling is O(org size)), so the card
  /// is the signal; one we've already acted on names no org of ours.
  const dropRevokedOrgs = (fetched: Notification[]) => {
    const revoked = fetched.some((n) => {
      if (n.notification_type_js() !== NotificationTypeJS.OrgAccessRevoked) {
        return false;
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- WASM interop: get_data_js() returns untyped JS object
      const data = n.get_data_js() as any;
      return useOrgContextStore
        .getState()
        .orgs.some((o) => o.org_id === data?.org_id);
    });
    if (revoked) void useOrgContextStore.getState().refreshOrgs();
  };

  /// Neither list polls from most pages, so someone else's grant or revoke is
  /// invisible until reload; the card is the signal.
  const refreshOnAccessCards = (fetched: Notification[]) => {
    const idsOf = (types: readonly NotificationTypeJS[]): string[] =>
      fetched
        .filter((n) => types.includes(n.notification_type_js()))
        .map((n) => n.notification_id_js());
    const { refreshSpaces, refreshModels, seen } = scanAccessCards(
      idsOf(SPACE_LIST_CARDS),
      idsOf(MODEL_LIST_CARDS),
      seenAccessCardsRef.current,
    );
    seenAccessCardsRef.current = seen;

    if (refreshSpaces) {
      void useOrgContextStore.getState().refreshSpaces();
      // A guest grant (D26) lands in Shared-with-me, whose store never polls.
      void useSharedWithMeStore.getState().refresh();
    }
    if (refreshModels) void invalidateModels();
  };

  const activeOrg = useOrgContextStore((s) =>
    s.orgs.find((o) => o.org_id === s.activeOrgId),
  );
  // Shared with me shows only when something is shared from outside the
  // caller's orgs (D32), so an empty page never becomes a permanent rail item.
  const sharedSpaces = useSharedWithMeStore((s) => s.spaces);
  const sharedStatus = useSharedWithMeStore((s) => s.status);
  const refreshSharedWithMe = useSharedWithMeStore((s) => s.refresh);
  const availableModels = useMainStateStore((s) => s.availableModels);
  const sharedCount = useMemo(() => {
    const external = selectExternalModels(availableModels);
    const granted = new Set(sharedSpaces.map((sp) => sp.space_id));
    const { standalone } = partitionSharedModels(external, granted);
    return sharedWithMeCount(sharedSpaces.length, standalone.length);
  }, [availableModels, sharedSpaces]);
  useEffect(() => {
    if (navigator.onLine) void refreshSharedWithMe();
  }, [refreshSharedWithMe]);
  // A warning, not a dashboard: personal org only, from QUOTA_WARNING_PERCENT.
  // A company org's usage lives on its settings page, with those who can act.
  const isPersonalOrg = !!activeOrg?.is_personal;
  // Whichever allowance is closest to full, not always storage.
  const railMeter = useMemo(
    () => nearestLimit(usage, QUOTA_WARNING_PERCENT),
    [usage],
  );
  const refreshUsage = useOrgContextStore((s) => s.refreshUsage);

  const refreshData = useCallback(() => {
    loadNotifications();
  }, []);

  useEffect(() => {
    if (navigator.onLine) {
      refreshData();
      void refreshUsage();
    }

    // Idle-only, not hidden-only: the browser already throttles hidden tabs.
    intervalRef.current = window.setInterval(() => {
      if (!navigator.onLine || !userActive()) return;
      refreshData();
    }, 30_000);
    // The backstop for an allowance spent by a colleague or an agent; a
    // refusal refreshes at once.
    storageIntervalRef.current = window.setInterval(() => {
      if (!navigator.onLine || !pollingAllowed()) return;
      void refreshUsage();
    }, 60000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      if (storageIntervalRef.current) {
        clearInterval(storageIntervalRef.current);
        storageIntervalRef.current = null;
      }
    };
  }, [refreshData, refreshUsage]);

  useResumeRefresh(
    useCallback(() => {
      if (navigator.onLine) {
        refreshData();
        void refreshUsage();
        // Catches space changes no card announces (a reclassification, a seal).
        // Not in `refreshData`, the 30s tick: each read is an FGA ListObjects.
        void useOrgContextStore.getState().refreshSpaces();
        void useSharedWithMeStore.getState().refresh();
      }
    }, [refreshData, refreshUsage]),
  );

  useEffect(() => {
    const handleNotificationsRefresh = () => {
      loadNotifications();
    };
    window.addEventListener(
      "notifications-refresh",
      handleNotificationsRefresh,
    );
    return () => {
      window.removeEventListener(
        "notifications-refresh",
        handleNotificationsRefresh,
      );
    };
  }, []);

  useEffect(() => {
    fetchAccountDetails(email, setAccountDetails);
  }, [email]);

  const profilePhoto = accountDetails?.account_details?.photo || "";

  // The flyout is fixed-positioned from its trigger's rect at open time, so
  // anything that moves that trigger strands it beside the wrong row.
  useEffect(() => {
    const scroller = navScrollRef.current;
    if (!scroller) return;
    const closeFlyouts = () => {
      setShowNotificationsDropdown(false);
      setNotificationsDropdownPersistent(false);
    };
    scroller.addEventListener("scroll", closeFlyouts, { passive: true });
    window.addEventListener("resize", closeFlyouts);
    return () => {
      scroller.removeEventListener("scroll", closeFlyouts);
      window.removeEventListener("resize", closeFlyouts);
    };
  }, []);

  const handleNotificationsClick = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    setNotificationsDropdownPersistent((prev) => {
      const next = !prev;
      setShowNotificationsDropdown(next);
      // An invite card's Accept/Decline reads the invitations list, which is
      // otherwise fetched only at init.
      if (next) void useOrgContextStore.getState().refreshInvites();
      return next;
    });
  };

  const handleNotificationsClose = () => {
    setShowNotificationsDropdown(false);
    setNotificationsDropdownPersistent(false);
  };

  const handleNotificationsDropdownEnter = () => {
    if (notificationsTimeoutRef.current)
      clearTimeout(notificationsTimeoutRef.current);
  };

  const handleNotificationsDropdownLeave = () => {
    if (!notificationsDropdownPersistent) setShowNotificationsDropdown(false);
  };

  const handleNotificationClick = (_notificationId: string) => {
    loadNotifications();
  };

  const handleLogout = () => {
    socket?.close();
    logout();
  };

  const hasUnreadNotifications = notifications.some(
    (notification) => notification.status_js() === NotificationStatus.Pending,
  );

  const sidebarItem = (
    Icon: React.ComponentType<{ className?: string }>,
    text: string,
    page?: string,
    overrides?: Partial<SidebarItem>,
  ): SidebarItem => ({
    icon: <Icon className="icon-md cursor-default" />,
    text,
    tooltip: text,
    page,
    ...overrides,
  });

  const pageItem = (
    Icon: React.ComponentType<{ className?: string }>,
    page: ShellPageKey,
  ): SidebarItem => sidebarItem(Icon, railName(page), page);

  const notificationsIcon = (
    <Badge
      color="primary"
      variant="dot"
      invisible={!hasUnreadNotifications}
      overlap="circular"
      sx={{
        "& .MuiBadge-dot": {
          backgroundColor: "var(--color-accent)",
          width: "6px !important",
          height: "6px !important",
          minWidth: "6px",
          minHeight: "6px",
          borderRadius: "50%",
        },
      }}
    >
      <NotificationsOutlinedIcon className="icon-md cursor-default" />
    </Badge>
  );

  const showSpaces = isOpen && !treeCollapsed;
  const filtering = useSpacesFilterStore((s) => s.query !== null) && isOpen;
  const spacePaths = useVisibleSpacePaths();
  const openSpaces = useCallback(
    (spaceId: string | null) => {
      setSelectedSpace(spaceId);
      navigateToPage("Overview");
    },
    [setSelectedSpace, navigateToPage],
  );
  // Collapsed, the rail has no room for the tree, so it opens beside the
  // Spaces icon: every level indented, since a flyout has no chevrons.
  const spaceFlyoutRows = useMemo<SidebarItem[]>(
    () =>
      isOpen
        ? []
        : spaceSubtree(spacePaths, null).map(({ path: { space }, depth }) => ({
            id: space.space_id,
            icon: (
              <span className="flex h-4 w-4 items-center justify-center">
                <SpaceDot color={spaceColor(space)} />
              </span>
            ),
            text: space.name,
            depth,
            isActive:
              selectedPage === "Overview" && selectedSpaceId === space.space_id,
            rightAction: space.sealed ? (
              <LockOutlinedIcon sx={{ fontSize: 11 }} />
            ) : undefined,
            onClick: () => openSpaces(space.space_id),
          })),
    [isOpen, spacePaths, selectedPage, selectedSpaceId, openSpaces],
  );
  const spacesItem: SidebarItem = {
    ...pageItem(DashboardOutlinedIcon, "Overview"),
    // Also clears the filter, which navigating to the open page would not.
    onClick: () => openSpaces(null),
    // A narrowed Overview is the space's row; only a hidden tree hands it back.
    isActive: selectedPage === "Overview" && (!selectedSpaceId || !showSpaces),
    leading: <SpacesFoldToggle />,
    trailing: <SpacesRootActions />,
    children: isOpen
      ? undefined
      : [
          {
            ...sidebarItem(DashboardOutlinedIcon, "All spaces"),
            isActive: selectedPage === "Overview" && !selectedSpaceId,
            onClick: () => openSpaces(null),
          },
          ...spaceFlyoutRows,
        ],
  };
  const homeItems: SidebarItem[] = [
    {
      ...sidebarItem(NotificationsOutlinedIcon, "Notifications", undefined),
      icon: notificationsIcon,
      hasDropdown: true,
      onClick: handleNotificationsClick,
      buttonRef: notificationsButtonRef,
      isActive: notificationsDropdownPersistent,
    },
    ...(filtering ? [] : [spacesItem]),
  ];

  const pageItems: SidebarItem[] = [
    ...(sharedStatus === "ready" && sharedCount > 0
      ? [pageItem(FolderSharedOutlinedIcon, "SharedWithMe")]
      : []),
    pageItem(LayersOutlinedIcon, "Datasets"),
    ...(canManagePublicCatalog
      ? [pageItem(PublicOutlinedIcon, "CatalogModels")]
      : []),
    pageItem(FunctionsIcon, "Recipes"),
    pageItem(StorageOutlinedIcon, "Connections"),
    ...(role === "mycor_dev_team"
      ? [
          {
            ...sidebarItem(AdminPanelSettingsOutlinedIcon, "Admin"),
            isActive: selectedPage.startsWith("Admin"),
            // A side flyout in both rail modes — the chevron points the way
            // it opens.
            rightAction: <ChevronRightIcon className="icon-sm" />,
            children: [
              pageItem(ListAltOutlinedIcon, "AdminInvitations"),
              pageItem(PeopleOutlineIcon, "AdminAccounts"),
              pageItem(CorporateFareOutlinedIcon, "AdminOrganizations"),
              pageItem(PersonOffOutlinedIcon, "AdminRemovedAccounts"),
              pageItem(SmartToyOutlinedIcon, "AdminServiceAccounts"),
              pageItem(HistoryOutlinedIcon, "AdminDataLifecycle"),
              pageItem(TroubleshootOutlinedIcon, "AdminAudit"),
              // Env-specific Grafana; unset locally, so the item only exists
              // on deployed builds.
              ...(import.meta.env.VITE_APP_MONITORING_URL
                ? [
                    {
                      ...sidebarItem(QueryStatsOutlinedIcon, "Monitoring"),
                      onClick: () =>
                        window.open(
                          import.meta.env.VITE_APP_MONITORING_URL,
                          "_blank",
                          "noopener,noreferrer",
                        ),
                      rightAction: <OpenInNewIcon className="icon-sm" />,
                    },
                  ]
                : []),
            ],
          },
        ]
      : []),
  ];

  const supportMenuItems: SidebarItem[] = [
    pageItem(DeleteOutlinedIcon, "Trash"),
    pageItem(VpnKeyOutlinedIcon, "Access"),
    sidebarItem(ContactSupportOutlinedIcon, "Support", undefined, {
      onClick: (e) => openSupport(e?.currentTarget.getBoundingClientRect().top),
    }),
  ];

  const handleItemClick = (page: string) => {
    navigateToPage(page);
  };

  const pinnedItems = (items: SidebarItem[]) => (
    <div className="sticky left-0 w-[100cqw]">
      <SidebarItems
        items={items}
        isOpen={isOpen}
        activeItem={selectedPage}
        onItemClick={handleItemClick}
        sidebarWidth={sidebarWidthVar}
      />
    </div>
  );

  useEffect(() => {
    return () => {
      if (dropdownTimeoutRef.current) {
        clearTimeout(dropdownTimeoutRef.current);
      }
      if (notificationsTimeoutRef.current) {
        clearTimeout(notificationsTimeoutRef.current);
      }
    };
  }, []);

  return (
    <>
      <div
        id="main-sidebar"
        className="sidebar"
        style={{
          width: "100%",
          boxShadow: isOpen
            ? "4px 0 12px -2px rgba(0, 0, 0, 0.06)"
            : "0 0 0 0 rgba(0, 0, 0, 0)",
        }}
      >
        <div
          className={`logo-area flex justify-center items-center item-height shrink-0${
            isOpen ? " logo-area--open" : ""
          }`}
        >
          {/* The link hangs off the logo so its reveal can't move the toggle. */}
          <div className="relative flex items-center">
            <RailToggle isOpen={isOpen} onToggle={toggleSidebar} />
            <a
              href="https://mycorr.app"
              target="_blank"
              rel="noopener noreferrer"
              className="info-chip logo-link"
              aria-hidden={!isOpen}
              tabIndex={isOpen ? 0 : -1}
            >
              <div className="info-chip__text">
                <span
                  className="flex items-center"
                  style={{ gap: "var(--spacing-xs)" }}
                >
                  <span>mycorr.app</span>
                  <OpenInNewIcon sx={{ fontSize: 10 }} />
                </span>
              </div>
            </a>
          </div>
        </div>

        <div className="flex flex-col h-full overflow-hidden">
          {isOpen && <OrgSwitcher />}
          {/* The rail's only scroller (`min-h-0`, or a long nav pushes the footer
              out). A deep tree scrolls it sideways; page items stay pinned at `100cqw`. */}
          <div
            ref={navScrollRef}
            className="@container flex-1 min-h-0 overflow-auto scrollbar-overlay-hover"
          >
            <div className="mt-6 w-max min-w-full">
              {pinnedItems(homeItems)}
              {filtering && (
                <div className="sticky left-0 w-[100cqw]">
                  <SpacesFilterField onPick={openSpaces} />
                </div>
              )}
              {/* Mounted while folded, so collapsing slides the tree shut. */}
              {isOpen && (
                <SpacesRail
                  selectedSpaceId={selectedSpaceId}
                  onSelectSpace={setSelectedSpace}
                />
              )}
              {pinnedItems(pageItems)}
            </div>
          </div>
          <Divider />
          <SidebarItems
            items={supportMenuItems}
            isOpen={isOpen}
            activeItem={selectedPage}
            onItemClick={handleItemClick}
          />
          <Divider />
          {isPersonalOrg && railMeter && (
            <>
              <StorageIndicator
                isOpen={isOpen}
                meter={railMeter}
                onSupportClick={openSupport}
                sidebarWidth={sidebarWidthVar}
              />
              <Divider />
            </>
          )}
          <UserProfileItem
            isOpen={isOpen}
            email={email}
            accountDetails={accountDetails}
            profilePhoto={profilePhoto}
            onLogout={handleLogout}
            onOpenSettings={() => setSettingsOpen(true)}
            sidebarWidth={sidebarWidthVar}
          />
        </div>
      </div>

      <div
        onMouseEnter={handleNotificationsDropdownEnter}
        onMouseLeave={handleNotificationsDropdownLeave}
      >
        <NotificationDropdown
          notifications={notifications}
          isVisible={showNotificationsDropdown}
          onClose={handleNotificationsClose}
          onNotificationClick={handleNotificationClick}
          onNotificationUpdate={loadNotifications}
          triggerRef={notificationsButtonRef as React.RefObject<HTMLElement>}
          sidebarWidth={sidebarWidthVar}
          onOpenSettings={() => setSettingsOpen(true)}
        />
      </div>

      <SettingsDialog
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        accountDetails={accountDetails}
        onAccountUpdated={() => fetchAccountDetails(email, setAccountDetails)}
      />
    </>
  );
};
